import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Badge, Empty, Pagination, STATUS_TONE, fmtDate, label } from '../components/ui.jsx';
import { t } from '../i18n/index.jsx';
import FundForm, { FUND_STATUSES, blankFund } from '../components/forms/FundForm.jsx';
import { IconPlus } from '../components/icons.jsx';
import DataTable from '../components/DataTable.jsx';
import ExportButton from '../components/ExportButton.jsx';
import { cachedFetch, invalidate } from '../api/cache.js';

const fmtMoney = (v) => (v == null || v === '' ? '—' : `€${Number(v).toLocaleString()}`);

const nameTemplate = (row) => (
  <div>
    <Link to={`/funds/${row.id}`} className="strong">{row.name}</Link>
    {row.reference_code && <div className="muted sm">{row.reference_code}</div>}
  </div>
);

const statusTemplate = (row) => <Badge tone={STATUS_TONE[row.status]}>{label(row.status)}</Badge>;

export default function Funds() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user.role === 'admin' || user.role === 'manager';
  const [list, setList] = useState(null);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    params.set('page', String(page));
    params.set('per_page', '20');
    const path = `/funds?${params}`;
    const res = await cachedFetch(path, () => api.get(path));
    setList(res.data);
    setMeta(res.meta || null);
  }, [status, search, page]);
  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);
  useEffect(() => { setPage(1); }, [status, search]);

  const columns = [
    { headerText: t('Fund'), width: 200, template: nameTemplate },
    { field: 'body_name', headerText: t('Body'), width: 140 },
    { field: 'client_count', headerText: t('Clients'), width: 90 },
    { field: 'total_budget', headerText: t('Budget'), width: 120, template: (r) => fmtMoney(r.total_budget) },
    { headerText: t('Status'), width: 110, template: statusTemplate },
    { field: 'close_date', headerText: t('Closes'), width: 120, template: (r) => fmtDate(r.close_date) },
  ];

  return (
    <div className="page fade-in">
      <div className="page-head">
        <h1 className="page-title">{t('Funds')}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ExportButton
            filename="funds"
            rows={list || []}
            columns={[
              { header: t('Fund'), field: 'name' },
              { header: t('Body'), field: 'body_name' },
              { header: t('Clients'), field: 'client_count' },
              { header: t('Budget'), field: 'total_budget' },
              { header: t('Status'), field: 'status' },
              { header: t('Closes'), field: 'close_date' },
            ]}
            disabled={!list?.length}
          />
          {canEdit && (
            <button type="button" className="btn primary" onClick={() => setEditing({ ...blankFund })}>
              <IconPlus width={15} height={15} /> {t('New fund')}
            </button>
          )}
        </div>
      </div>

      <div className="toolbar">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="search">
          <input placeholder={t('Search name or reference code…')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </form>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('All statuses')}</option>
          {FUND_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
      </div>

      {!list ? (
        <DataTable data={[]} columns={columns} loading />
      ) : list.length === 0 ? (
        <Empty>{t('No funds yet.')}</Empty>
      ) : (
        <DataTable
          data={list}
          columns={columns}
          onRowClick={(row) => navigate(`/funds/${row.id}`)}
          onRowAction={(row) => navigate(`/funds/${row.id}`)}
        />
      )}

      <Pagination meta={meta} onPage={setPage} />

      {editing && (
        <FundForm
          initial={editing}
          isAdmin={user.role === 'admin'}
          onClose={() => setEditing(null)}
          onSaved={() => { invalidate('/funds'); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}